import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const PERMISSIONS_STORAGE_KEY = 'permissions';

type PermissionCondition = 'AND' | 'OR';

/**
 * Hook that guards a route based on user permissions.
 * Redirects to the specified route if the user lacks the required permission(s).
 *
 * @param requiredPermissions - Array of permission keys to check against.
 * @param condition - 'OR' (default) = any one permission is enough, 'AND' = all permissions required.
 * @param redirectRoute - Route to redirect to if permission check fails (default: '/access-denied').
 *
 * @example
 * // Redirect if user doesn't have at least one of these permissions
 * usePermissionRouteAccess(['view_users', 'manage_users']);
 *
 * // Redirect if user doesn't have ALL of these permissions
 * usePermissionRouteAccess(['create_users', 'delete_users'], 'AND');
 *
 * // Custom redirect route
 * usePermissionRouteAccess(['admin_access'], 'OR', '/unauthorized');
 */
const usePermissionRouteAccess = (
  requiredPermissions: string[],
  condition: PermissionCondition = 'OR',
  redirectRoute: string = '/access-denied',
) => {
  const navigate = useNavigate();

  useEffect(() => {
    const permissions: string[] = JSON.parse(
      sessionStorage.getItem(PERMISSIONS_STORAGE_KEY) || '[]',
    );

    let hasPermission: boolean;

    if (condition === 'AND') {
      hasPermission = requiredPermissions.every((permission) =>
        permissions.includes(permission),
      );
    } else {
      hasPermission = requiredPermissions.some((permission) =>
        permissions.includes(permission),
      );
    }

    if (!hasPermission) {
      navigate(redirectRoute, { replace: true });
    }
  }, [navigate, requiredPermissions, condition, redirectRoute]);
};

export default usePermissionRouteAccess;
